import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import '../../components/LopButton/LopButton.css'; // .lop-btn__spinner
import { t } from '../../i18n/t';
import {
  getCachedChapter,
  loadAllChapters,
  loadChapter,
  loadManifest,
  type ChapterContent,
  type GuideChapterMeta,
  type GuideManifest,
} from './guideContent';
import { MarkdownView, sectionAnchorId } from './MarkdownView';
import './UserGuide.css';

const PRINT_BODY_CLASS = 'user-guide-printing';

function useManifest() {
  const [state, setState] = useState<{ manifest: GuideManifest | null; error: boolean }>({
    manifest: null,
    error: false,
  });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setState((s) => (s.error ? { manifest: null, error: false } : s));
    loadManifest()
      .then((manifest) => !cancelled && setState({ manifest, error: false }))
      .catch(() => !cancelled && setState({ manifest: null, error: true }));
    return () => {
      cancelled = true;
    };
  }, [attempt]);
  return { ...state, retry: () => setAttempt((a) => a + 1) };
}

function useChapter(chapter: GuideChapterMeta | undefined) {
  const [content, setContent] = useState<ChapterContent | undefined>(() =>
    chapter ? getCachedChapter(chapter.id) : undefined,
  );
  useEffect(() => {
    if (!chapter) return;
    const cached = getCachedChapter(chapter.id);
    if (cached) {
      setContent(cached);
      return;
    }
    setContent(undefined);
    let cancelled = false;
    loadChapter(chapter).then((c) => !cancelled && setContent(c));
    return () => {
      cancelled = true;
    };
  }, [chapter]);
  // Guard against a stale value from the previous chapter during the switch.
  return chapter && content && (content.status === 'missing' || content.doc) ? content : undefined;
}

export function UserGuidePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { manifest, error, retry } = useManifest();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [printData, setPrintData] = useState<Map<string, ChapterContent> | null>(null);
  const [preparing, setPreparing] = useState(false);

  const chapterParam = new URLSearchParams(location.search).get('chapter');
  const chapters = useMemo(() => manifest?.chapters ?? [], [manifest]);
  const index = Math.max(
    0,
    chapters.findIndex((c) => c.id === chapterParam),
  );
  const chapter: GuideChapterMeta | undefined = chapters[index];
  const content = useChapter(chapter);

  const selectChapter = useCallback(
    (id: string) => {
      // replace: switching chapters must not spam browser history. The tab key
      // identity is just the pathname (chapter is not an identity param).
      navigate({ pathname: location.pathname, search: `?chapter=${encodeURIComponent(id)}` }, { replace: true });
    },
    [navigate, location.pathname],
  );

  // New chapter -> back to the top, reset scroll-spy.
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
    setActiveSection(null);
  }, [chapter?.id]);

  const sections = useMemo(() => (content?.status === 'ok' ? content.doc.sections : []), [content]);

  // Scroll-spy: highlight the last section heading above the top of the viewport.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !chapter || sections.length === 0) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const top = el.getBoundingClientRect().top + 120;
      let current: number | null = null;
      for (const s of sections) {
        const h = document.getElementById(sectionAnchorId(`ug-${chapter.id}`, s.index));
        if (h && h.getBoundingClientRect().top <= top) current = s.index;
      }
      setActiveSection(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [chapter, sections]);

  function scrollToSection(i: number) {
    if (!chapter) return;
    // The scroll container is this page's own div (inside the tab pane), not window.
    document
      .getElementById(sectionAnchorId(`ug-${chapter.id}`, i))
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    setActiveSection(i);
  }

  // ---- PDF via print ----
  async function handlePdf() {
    if (!manifest || preparing) return;
    setPreparing(true);
    try {
      setPrintData(await loadAllChapters(manifest));
    } finally {
      setPreparing(false);
    }
  }

  useEffect(() => {
    if (!printData) return;
    let cancelled = false;
    const root = document.body;
    const cleanup = () => {
      root.classList.remove(PRINT_BODY_CLASS);
      setPrintData(null);
    };
    window.addEventListener('afterprint', cleanup, { once: true });
    root.classList.add(PRINT_BODY_CLASS);
    (async () => {
      // Let the print container render and its images settle before the dialog opens.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const imgs = Array.from(document.querySelectorAll<HTMLImageElement>('.user-guide-print img'));
      await Promise.race([
        Promise.all(imgs.map((im) => (im.complete ? undefined : new Promise((r) => { im.onload = im.onerror = r; })))),
        new Promise((r) => setTimeout(r, 4000)),
      ]);
      if (!cancelled) window.print();
    })();
    return () => {
      cancelled = true;
      window.removeEventListener('afterprint', cleanup);
      root.classList.remove(PRINT_BODY_CLASS);
    };
  }, [printData]);

  if (error) {
    return (
      <div className="user-guide" ref={scrollRef}>
        <div className="user-guide__state" role="alert">
          <p>{t('userGuide.loadError')}</p>
          <button type="button" className="user-guide__btn" onClick={retry}>
            {t('userGuide.retry')}
          </button>
        </div>
      </div>
    );
  }
  if (!manifest || !chapter) {
    return (
      <div className="user-guide" ref={scrollRef}>
        <div className="user-guide__state">{t('userGuide.loading')}</div>
      </div>
    );
  }

  const prev = chapters[index - 1];
  const next = chapters[index + 1];
  const asOfText = t('userGuide.asOf', { date: formatDate(manifest.asOf.date), version: manifest.asOf.appVersion });

  return (
    <div className="user-guide" ref={scrollRef}>
      <h1 className="user-guide__title">{t('userGuide.title')}</h1>
      <div className="user-guide__layout">
        <nav className="user-guide__toc" aria-label={t('userGuide.tocAriaLabel')}>
          <button
            type="button"
            className="user-guide__pdf"
            onClick={handlePdf}
            disabled={preparing}
            aria-busy={preparing ? true : undefined}
            title={t('userGuide.downloadPdfHint')}
          >
            <span>{t('userGuide.downloadPdf')}</span>
            {preparing ? <Loader2 size={14} className="lop-btn__spinner" aria-hidden="true" /> : <Download size={14} aria-hidden="true" />}
          </button>
          <ol className="user-guide__toc-list">
            {chapters.map((c, i) => {
              const active = i === index;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`user-guide__toc-chapter${active ? ' is-active' : ''}`}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => selectChapter(c.id)}
                  >
                    <span className="user-guide__toc-num">{c.number}</span>
                    <span>{c.title}</span>
                  </button>
                  {active && sections.length > 0 && (
                    <ul className="user-guide__toc-sections">
                      {sections.map((s) => (
                        <li key={s.index}>
                          <button
                            type="button"
                            className={`user-guide__toc-section${activeSection === s.index ? ' is-active' : ''}`}
                            onClick={() => scrollToSection(s.index)}
                          >
                            {s.text}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        <main className="user-guide__main">
          <header className="user-guide__chapter-header">
            <span className="user-guide__badge" aria-label={t('userGuide.chapterNumberLabel', { number: chapter.number })}>
              {chapter.number}
            </span>
            <h2 className="user-guide__chapter-title">{chapter.title}</h2>
          </header>
          <article className="user-guide__body">
            {content === undefined ? (
              <p className="user-guide__muted">{t('userGuide.loading')}</p>
            ) : content.status === 'missing' ? (
              <p className="user-guide__muted">{t('userGuide.chapterUnavailable')}</p>
            ) : (
              <MarkdownView blocks={content.doc.blocks} idPrefix={`ug-${chapter.id}`} />
            )}
          </article>
          <div className="user-guide__pager" role="navigation" aria-label={t('userGuide.chapterNav')}>
            {prev ? (
              <button type="button" className="user-guide__pager-btn" data-testid="guide-prev" onClick={() => selectChapter(prev.id)}>
                <ChevronRight size={18} aria-hidden="true" />
                <span>
                  <small>{t('userGuide.previous')}</small>
                  <strong>{prev.title}</strong>
                </span>
              </button>
            ) : (
              <span />
            )}
            {next ? (
              <button type="button" className="user-guide__pager-btn user-guide__pager-btn--next" data-testid="guide-next" onClick={() => selectChapter(next.id)}>
                <span>
                  <small>{t('userGuide.next')}</small>
                  <strong>{next.title}</strong>
                </span>
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
            ) : (
              <span />
            )}
          </div>
        </main>
      </div>
      {printData &&
        createPortal(
          <PrintView manifest={manifest} data={printData} asOfText={asOfText} />,
          document.body,
        )}
    </div>
  );
}

function PrintView({
  manifest,
  data,
  asOfText,
}: {
  manifest: GuideManifest;
  data: Map<string, ChapterContent>;
  asOfText: string;
}) {
  return (
    <div className="user-guide-print" dir="rtl">
      <section className="ugp-cover">
        <h1>{manifest.title}</h1>
        <p>{asOfText}</p>
      </section>
      <section className="ugp-toc">
        <h2>{t('userGuide.tocPageTitle')}</h2>
        <ol>
          {manifest.chapters.map((c) => (
            <li key={c.id}>
              <span className="ugp-toc-num">{c.number}</span>
              <span>{c.title}</span>
            </li>
          ))}
        </ol>
      </section>
      {manifest.chapters.map((c) => {
        const content = data.get(c.id);
        return (
          <section key={c.id} className="ugp-chapter user-guide__body">
            <header className="user-guide__chapter-header">
              <span className="user-guide__badge">{c.number}</span>
              <h2 className="user-guide__chapter-title">{c.title}</h2>
            </header>
            {content?.status === 'ok' ? (
              <MarkdownView blocks={content.doc.blocks} idPrefix={`ugp-${c.id}`} />
            ) : (
              <p>{t('userGuide.chapterUnavailable')}</p>
            )}
          </section>
        );
      })}
    </div>
  );
}

function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
